import React, { useState, useEffect } from 'react';
import { APP_PATHS } from '../../config/constants';
import './ResponsiveImage.css';

const ResponsiveImage = ({
  desktopImage,
  mobileImage,
  fallbackImage,
  alt = 'Image',
  className = '',
  layout = 'responsive', // 'responsive', 'desktop-side', 'mobile-top', 'card-layout'
  aspectRatio = 'auto', // 'auto', '16:9', '21:9', '32:9'
  desktopAspectRatio = '16:9', // Aspect ratio for desktop
  mobileAspectRatio = '21:9', // Aspect ratio for mobile
  position = 'auto', // 'auto', 'left', 'right', 'top', 'bottom'
  language = 'ar', // Default language prop
  onError,
  onLoad,
  // Filter out any props that shouldn't be passed to DOM elements
  objectFit, // Remove this prop as it's handled by CSS
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageError, setCurrentImageError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determine which image to use based on screen size
  const getImageSource = () => {
    if (currentImageError) {
      return fallbackImage || APP_PATHS.PUBLIC_ASSETS.LOGO_ONLY;
    }

    if (isMobile && mobileImage) {
      return mobileImage;
    }

    if (!isMobile && desktopImage) {
      return desktopImage;
    }

    // Fallback logic
    if (desktopImage) return desktopImage;
    if (mobileImage) return mobileImage;
    return fallbackImage || APP_PATHS.PUBLIC_ASSETS.LOGO_ONLY;
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    if (onLoad) onLoad();
  };

  const handleImageError = (e) => {
    setIsLoading(false);
    setCurrentImageError(true);
    
    // Try fallback image if not already using it
    if (!currentImageError && fallbackImage) {
      e.target.src = fallbackImage;
      return;
    }
    
    // Final fallback to logo
    e.target.src = APP_PATHS.PUBLIC_ASSETS.LOGO_ONLY;
    
    if (onError) onError(e);
  };

  const imageSource = getImageSource();
  const isRTL = language === 'ar';

  // Generate CSS classes
  const getAspectRatioClass = () => {
    if (aspectRatio !== 'auto') {
      return `aspect-${aspectRatio.replace(':', '-')}`;
    }

    // Use responsive aspect ratios
    if (isMobile && mobileAspectRatio !== 'auto') {
      return `mobile-aspect-${mobileAspectRatio.replace(':', '-')}`;
    }

    if (!isMobile && desktopAspectRatio !== 'auto') {
      return `desktop-aspect-${desktopAspectRatio.replace(':', '-')}`;
    }

    // Return aspect-auto for auto aspect ratio
    return 'aspect-auto';
  };

  const containerClasses = [
    'responsive-image-container',
    className,
    layout,
    getAspectRatioClass(),
    position !== 'auto' ? `image-position-${position}` : '',
    isRTL ? 'rtl' : 'ltr',
    isMobile ? 'mobile' : 'desktop',
    isLoading ? 'loading' : ''
  ].filter(Boolean).join(' ');

  // Filter out component-specific props that shouldn't be passed to DOM
  const {
    desktopImage: _desktopImage,
    mobileImage: _mobileImage,
    fallbackImage: _fallbackImage,
    layout: _layout,
    aspectRatio: _aspectRatio,
    desktopAspectRatio: _desktopAspectRatio,
    mobileAspectRatio: _mobileAspectRatio,
    position: _position,
    language: _language,
    onError: _onError,
    onLoad: _onLoad,
    objectFit: _objectFit,
    ...domProps
  } = props;

  return (
    <div className={containerClasses} {...domProps}>
      {isLoading && (
        <div className="image-loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      <img
        src={imageSource}
        alt={alt}
        className={`responsive-image ${isLoading ? 'loading' : ''}`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      
      {/* Debug info for development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-info">
          <small>
            {isMobile ? 'Mobile' : 'Desktop'} | 
            {isMobile && mobileImage ? 'Mobile Image' : 
             !isMobile && desktopImage ? 'Desktop Image' : 'Fallback'}
          </small>
        </div>
      )}
    </div>
  );
};

export default ResponsiveImage;
